/**
 * SlotEditor — modal for adding/editing claims on a single matrix cell.
 *
 * Opened when the user clicks a cell in MatrixViewer edit mode. Same
 * editor handles both:
 *   - empty cell: shows the "Add claim" form only
 *   - filled cell: lists existing claims (each editable / removable) +
 *     "Add another" appender for co-ownership
 *
 * The cell's (kind, channel, ccNumber|noteLabel) is fixed by the click
 * — to move a claim you delete and re-add elsewhere.
 */

import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import type { MatrixClaim, MatrixDevice, MatrixSlot } from '@opbuttons/schema';
import {
  matrixLibrary,
  matrixDevices,
  addClaim,
  updateClaim,
  removeClaim,
  removeSlot,
  type SlotLocator,
} from '../matrix/store.ts';

interface Props {
  locator: SlotLocator;
  onClose: () => void;
}

export function SlotEditor(props: Props) {
  const library = matrixLibrary;
  const devices = matrixDevices;

  const slot = createMemo<MatrixSlot | undefined>(() => {
    const lib = library();
    return lib.slots.find((s) => {
      if (s.channel !== props.locator.channel) return false;
      if (props.locator.kind === 'cc') {
        return s.kind === 'cc' && s.ccNumber === props.locator.ccNumber;
      }
      if (props.locator.kind === 'pc') {
        return s.kind === 'pc' && s.pcNumber === props.locator.pcNumber;
      }
      return s.kind === 'note' && s.noteLabel === props.locator.noteLabel;
    });
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') props.onClose();
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  const cellTitle = createMemo(() => {
    const l = props.locator;
    if (l.kind === 'cc') return `CC ${l.ccNumber} · ch ${l.channel}`;
    if (l.kind === 'pc') return `PC ${l.pcNumber} · ch ${l.channel}`;
    return `Note ${l.noteLabel} · ch ${l.channel}`;
  });

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div
        class="slot-editor-overlay"
        onClick={props.onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit slot ${cellTitle()}`}
      >
        <div class="slot-editor-modal" onClick={(e) => e.stopPropagation()}>
          <header class="slot-editor-head">
            <div class="slot-editor-title">
              <span class="slot-editor-cell">{cellTitle()}</span>
              <span class="slot-editor-meta">
                <Show when={slot()} fallback="Empty - add the first claim below.">
                  {(s) => `${s().claims.length} claim${s().claims.length === 1 ? '' : 's'}`}
                </Show>
              </span>
            </div>
            <button
              type="button"
              class="slot-editor-close"
              onClick={props.onClose}
              title="Close (Esc)"
              aria-label="Close"
            >
              ×
            </button>
          </header>

          <Show when={slot() && slot()!.claims.length > 0}>
            <div class="slot-editor-claims">
              <For each={slot()!.claims}>
                {(claim) => (
                  <ClaimRow
                    claim={claim}
                    devices={devices()}
                    onUpdate={(patch) => updateClaim(claim.id, patch)}
                    onRemove={() => removeClaim(claim.id)}
                  />
                )}
              </For>
              <button
                type="button"
                class="slot-editor-remove-slot"
                onClick={() => {
                  const s = slot();
                  if (s) removeSlot(s.id);
                  props.onClose();
                }}
                title="Remove all claims from this cell"
              >
                Clear cell
              </button>
            </div>
          </Show>

          <ClaimAdder
            label={slot() && slot()!.claims.length > 0 ? 'Add another (co-claim)' : 'Add claim'}
            devices={devices()}
            onSubmit={(input) => {
              if (devices().length === 0) return;
              addClaim(props.locator, input);
            }}
            disabled={devices().length === 0}
          />

          <Show when={devices().length === 0}>
            <p class="slot-editor-empty-hint">
              No devices yet. Add one from the legend at the top of the matrix viewer first.
            </p>
          </Show>
        </div>
      </div>
    </Portal>
  );
}

function ClaimRow(props: {
  claim: MatrixClaim;
  devices: MatrixDevice[];
  onUpdate: (patch: Partial<Pick<MatrixClaim, 'controlLabel' | 'notes'>>) => void;
  onRemove: () => void;
}) {
  const device = createMemo(() =>
    props.devices.find((d) => d.id === props.claim.deviceId),
  );
  return (
    <div class="slot-editor-claim" data-hue={device()?.color ?? ''}>
      <div class="slot-editor-claim-head">
        <span class="slot-editor-claim-device">
          <span class="slot-editor-claim-swatch" data-hue={device()?.color ?? ''} />
          {device()?.name ?? '(deleted device)'}
        </span>
        <button
          type="button"
          class="slot-editor-claim-remove"
          onClick={props.onRemove}
          title="Remove this claim from the cell"
        >
          Remove
        </button>
      </div>
      <label class="slot-editor-field">
        <span class="slot-editor-label">Control label</span>
        <input
          type="text"
          class="slot-editor-input"
          value={props.claim.controlLabel}
          onInput={(e) => props.onUpdate({ controlLabel: e.currentTarget.value })}
          placeholder="e.g. Fader 1"
          maxlength={64}
        />
      </label>
      <label class="slot-editor-field">
        <span class="slot-editor-label">Notes (optional)</span>
        <input
          type="text"
          class="slot-editor-input"
          value={props.claim.notes ?? ''}
          onInput={(e) => props.onUpdate({ notes: e.currentTarget.value })}
          placeholder="Purpose, gig assignment, anything."
        />
      </label>
    </div>
  );
}

function ClaimAdder(props: {
  label: string;
  devices: MatrixDevice[];
  onSubmit: (input: { deviceId: string; controlLabel: string; notes?: string }) => void;
  disabled: boolean;
}) {
  const [deviceId, setDeviceId] = createSignal<string>('');
  const [controlLabel, setControlLabel] = createSignal('');
  const [notes, setNotes] = createSignal('');

  // Default device = first one in the list (set on mount once devices()
  // resolves). Updating the prop after mount is rare in this UI so a
  // memo isn't needed.
  if (deviceId() === '' && props.devices.length > 0) {
    setDeviceId(props.devices[0]!.id);
  }

  function submit(): void {
    const did = deviceId();
    const label = controlLabel().trim();
    if (!did || !label) return;
    const input = {
      deviceId: did,
      controlLabel: label,
      ...(notes().trim() ? { notes: notes() } : {}),
    };
    props.onSubmit(input);
    setControlLabel('');
    setNotes('');
  }

  return (
    <div class="slot-editor-add" data-disabled={props.disabled ? 'true' : 'false'}>
      <div class="slot-editor-add-head">{props.label}</div>
      <div class="slot-editor-add-row">
        <label class="slot-editor-field slot-editor-field-grow">
          <span class="slot-editor-label">Device</span>
          <select
            class="slot-editor-input"
            value={deviceId()}
            onInput={(e) => setDeviceId(e.currentTarget.value)}
            disabled={props.disabled}
          >
            <For each={props.devices}>
              {(d) => <option value={d.id}>{d.name}</option>}
            </For>
          </select>
        </label>
        <label class="slot-editor-field slot-editor-field-grow">
          <span class="slot-editor-label">Control label</span>
          <input
            type="text"
            class="slot-editor-input"
            value={controlLabel()}
            onInput={(e) => setControlLabel(e.currentTarget.value)}
            placeholder="e.g. Fader 1"
            disabled={props.disabled}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
        </label>
      </div>
      <label class="slot-editor-field">
        <span class="slot-editor-label">Notes (optional)</span>
        <input
          type="text"
          class="slot-editor-input"
          value={notes()}
          onInput={(e) => setNotes(e.currentTarget.value)}
          placeholder="Purpose, gig assignment, anything."
          disabled={props.disabled}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
      </label>
      <button
        type="button"
        class="slot-editor-add-submit"
        onClick={submit}
        disabled={props.disabled || !controlLabel().trim()}
      >
        {props.label.startsWith('Add another') ? 'Add co-claim' : 'Claim cell'}
      </button>
    </div>
  );
}
